import PropTypes from "prop-types";
import { Box } from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import {
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  ExportPrint,
  useGridApiContext,
  useGridRootProps,
} from "@mui/x-data-grid";
import { useAsyncDataGridExport } from "./AsyncDataGridExportContext.js";
import AsyncDataGridExportMenu from "./AsyncDataGridExportMenu.jsx";
import AsyncDataGridQuickFilter from "./AsyncDataGridQuickFilter.jsx";
import AsyncDataGridPresetsToolbar from "./dataGridPresets/AsyncDataGridPresetsToolbar.jsx";
import { useAsyncDataGridPresets } from "./dataGridPresets/AsyncDataGridPresetsContext.js";

function ToolbarDivider() {
  return (
    <Box
      sx={{
        alignSelf: "center",
        borderLeft: 1,
        borderColor: "divider",
        height: 24,
        mx: 0.5,
      }}
    />
  );
}

/**
 * Custom DataGrid toolbar with print, custom export menu, columns, filters, and quick filter.
 *
 * @param {Object} props
 * @param {boolean} [props.showQuickFilter]
 * @param {Object} [props.quickFilterProps]
 * @param {import("react").ReactNode} [props.beforeQuickFilter] - Controls rendered immediately left of the quick filter
 */
function AsyncDataGridToolbar({ showQuickFilter = true, quickFilterProps, beforeQuickFilter = null, ...other }) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const { hideExport } = useAsyncDataGridExport();
  const presetsContext = useAsyncDataGridPresets();
  const Tooltip = rootProps.slots.baseTooltip;
  const ColumnSelectorIcon = rootProps.slots.columnSelectorIcon;
  const OpenFilterButtonIcon = rootProps.slots.openFilterButtonIcon;
  const Badge = rootProps.slots.baseBadge;

  const showColumns = !rootProps.disableColumnSelector;
  const showFilters = !rootProps.disableColumnFilter;
  const showExportSection = showColumns || showFilters;
  const hasLeadingControls = Boolean(presetsContext) || showColumns || showFilters || !hideExport;

  return (
    <Toolbar {...other}>
      {presetsContext && (
        <>
          <AsyncDataGridPresetsToolbar />
          <ToolbarDivider />
        </>
      )}
      {showColumns && (
        <Tooltip title={apiRef.current.getLocaleText("toolbarColumns")}>
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <ColumnSelectorIcon fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>
      )}
      {showFilters && (
        <Tooltip title={apiRef.current.getLocaleText("toolbarFilters")}>
          <FilterPanelTrigger
            render={(triggerProps, state) => (
              <ToolbarButton {...triggerProps} color={state.filterCount > 0 ? "primary" : "default"}>
                <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                  <OpenFilterButtonIcon fontSize="small" />
                </Badge>
              </ToolbarButton>
            )}
          />
        </Tooltip>
      )}
      {!hideExport && (
        <>
          {showExportSection && <ToolbarDivider />}
          <Tooltip title={apiRef.current.getLocaleText("toolbarExportPrint")}>
            <ExportPrint render={<ToolbarButton />}>
              <PrintOutlinedIcon fontSize="small" />
            </ExportPrint>
          </Tooltip>
          <Tooltip title={apiRef.current.getLocaleText("toolbarExport")}>
            <span>
              <AsyncDataGridExportMenu />
            </span>
          </Tooltip>
        </>
      )}
      {beforeQuickFilter && (
        <>
          {hasLeadingControls && <ToolbarDivider />}
          {beforeQuickFilter}
        </>
      )}
      {showQuickFilter && (
        <>
          {(hasLeadingControls || beforeQuickFilter) && <ToolbarDivider />}
          <AsyncDataGridQuickFilter {...quickFilterProps} />
        </>
      )}
    </Toolbar>
  );
}

AsyncDataGridToolbar.propTypes = {
  showQuickFilter: PropTypes.bool,
  quickFilterProps: PropTypes.object,
  beforeQuickFilter: PropTypes.node,
};

export default AsyncDataGridToolbar;
